/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable max-statements */
import R from 'ramda';

import { Thunk } from 'ha/myredux/types';
import { Language } from 'ha/i18n';
import { setRemarketingTag } from 'ha/modules/Analytics/actions';
import { LocaleToCanonicalCollections } from 'ha/constants/SearchPageCollections';
import {
  TypeValue,
  Filters,
  RoomsValue,
  RegistrationFilterValue,
  BillsFilterValue,
  FurnitureFilterValue,
  SuitableForFilterValue,
} from 'ha/types/SearchFilters';
import { Currency } from 'ha/constants/Currencies';

import { Actions, PageType } from '../constants';
import {
  getMapLocation,
  getSearchParams,
  getDateFilter,
  getCityCanonical,
  getCountryCode,
  getSuitableForFilter,
  getRegistrationFilter,
  getFurnitureFilter,
  getBillsFilter,
  getCategoriesFilter,
  getBedroomCount,
} from '../selectors';
import matchCategory from '../utils/matchCategory';

interface QueryParams {
  page: number | null;
  openMap: boolean | null;
  categories?: TypeValue[];
  furniture?: FurnitureFilterValue;
  bills?: BillsFilterValue;
  registration?: RegistrationFilterValue;
  suitableFor?: SuitableForFilterValue;
  bedroomCount?: RoomsValue[];
  currency?: Currency;
  startDate?: string;
  endDate?: string;
  priceMin?: number;
  priceMax?: number;
}

interface FinishFilters {
  queryParams: QueryParams;
  pathname?: string;
}

const changeFilters = (payload: FinishFilters) => ({
  type: Actions.CHANGE_FILTERS,
  payload,
  meta: {
    ...payload,
    mergeQueryParams: true,
  },
  error: false,
});

const categorySlug = (lang: Language, type: string) =>
  LocaleToCanonicalCollections[lang][type];

export const trackFiltersUpdate = ({
  categories,
  startDate,
  endDate,
}: QueryParams): Thunk => (dispatch, getState) => {
  const state = getState();
  const cityCanonical = getCityCanonical(state);
  const countryCode = getCountryCode(state);
  const { categories: prevCategories } = getSearchParams(state);
  const { endDate: prevEndDate, startDate: prevStartDate } = getDateFilter(
    state,
  );

  const categoriesChanged = !R.equals(prevCategories, categories);
  const datesChanges =
    (startDate !== undefined || endDate !== undefined) &&
    !(startDate === prevStartDate && endDate === prevEndDate);

  if (categoriesChanged || datesChanges) {
    dispatch(
      setRemarketingTag(
        {
          hrental_pagetype: PageType,
          hrental_city: cityCanonical,
          hrental_country: countryCode,
          hrental_startdate: datesChanges ? startDate : prevStartDate,
          hrental_enddate: datesChanges ? endDate : prevEndDate,
          hrental_search_category: categoriesChanged
            ? categories
            : prevCategories,
        },
        { nonInteraction: 0 },
      ),
    );
  }
};

const onChangeFilters = ({
  price,
  types,
  registration,
  rules,
  recentlyAdded,
  furniture,
  bills,
  rooms,
  dates,
  gender,
  suitableFor,
  intl,
  sorting,
}: Filters): Thunk => (dispatch, getState) => {
  const state = getState();

  const queryParams: QueryParams = {
    page: null,
    openMap: null,
    bedroomCount: getBedroomCount(state) || [],
    categories: getCategoriesFilter(state),
    bills: getBillsFilter(state),
    furniture: getFurnitureFilter(state),
    registration: getRegistrationFilter(state),
    suitableFor: getSuitableForFilter(state),
  };

  if (types) {
    Object.assign(queryParams, { categories: types });
  }

  if (furniture !== undefined) {
    Object.assign(queryParams, { furniture });
  }

  if (bills !== undefined) {
    Object.assign(queryParams, { bills });
  }

  if (registration !== undefined) {
    Object.assign(queryParams, { registration });
  }

  if (recentlyAdded !== undefined) {
    Object.assign(queryParams, { recentlyAdded });
  }

  if (suitableFor) {
    Object.assign(queryParams, { suitableFor });
  }

  if (rules) {
    Object.assign(queryParams, { rules });
  }

  if (gender) {
    Object.assign(queryParams, { gender });
  }

  if (sorting) {
    Object.assign(queryParams, { sorting });
  }

  if (rooms && rooms.bedroomCount.length !== 0) {
    Object.assign(queryParams, { bedroomCount: rooms.bedroomCount });
  }

  if (price) {
    Object.assign(queryParams, {
      currency: price.currency,
      priceMin: price.min,
      priceMax: price.max,
    });
  }

  if (dates) {
    Object.assign(queryParams, dates);
  }

  dispatch(trackFiltersUpdate(queryParams));

  const category = matchCategory({
    types,
    furniture,
    rooms,
    price,
    bills,
    suitableFor,
    dates,
  });

  if (category && intl) {
    const { urlResolver, lang } = intl;
    const location = getMapLocation(state);
    const urlBase = `/s/${location}`;

    const url = `${urlBase}/${categorySlug(lang, category)}`;
    const pathname = urlResolver.formatHrefURL(url);

    dispatch(
      changeFilters({
        pathname,
        queryParams: {
          ...queryParams,
          // reset all current query params in the URL
          page: null,
          openMap: null,
          bedroomCount: [],
          categories: undefined,
          bills: undefined,
          furniture: undefined,
          registration: undefined,
          suitableFor: undefined,
        },
      }),
    );

    return;
  }

  const filters: FinishFilters = {
    queryParams,
  };

  if (intl) {
    const { urlResolver } = intl;
    const location = getMapLocation(state);
    const urlBase = `/s/${location}`;
    const pathname = urlResolver.formatHrefURL(urlBase);

    Object.assign(filters, { pathname });
  }

  filters.queryParams.currency =
    filters.queryParams.currency !== 'EUR'
      ? filters.queryParams.currency
      : undefined;

  dispatch(changeFilters(filters));
};

export default onChangeFilters;
